import _ from 'underscore';
import Promise from 'bluebird';
import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ConfirmDialog } from '@bingads-webui-campaign-react/confirm-dialog';

const DeleteConfirmDialog = ({
  showConfirmDialog,
  setShowConfirmDialog,
  deleteAssetApi,
  onSuccess,
  i18n,
}) => {
  const [showUIBlocker, setShowUIBlocker] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const closeConfirmDialog = () => {
    setErrorMessage('');
    setShowConfirmDialog(false);
    setShowUIBlocker(false);
  };

  const onDelete = () => {
    setShowUIBlocker(true);
    deleteAssetApi().then(() => {
      onSuccess();
      closeConfirmDialog();
    }).catch((response) => {
      const { errors } = response || {};
      const err = _.first(errors) || {};
      if (err.Code === 'AssetIsUsed') {
        setErrorMessage(i18n.getString(_TL_("You can't delete this image because it's being used by other campaigns, ads, or image extensions.")));
      } else {
        setErrorMessage(i18n.getString(_TL_('Your image couldn\'t be deleted. Please try again or contact support.')));
      }
    }).finally(() => {
      setShowUIBlocker(false);
    });
  };

  const onCancel = () => {
    closeConfirmDialog();
  };

  return showConfirmDialog ? (
    <ConfirmDialog
      isDisplayed={showConfirmDialog}
      title={i18n.getString(_TL_('Are you sure you want to delete selected image?'))}
      errorText={errorMessage}
      confirmString={i18n.getString(_TL_('OK'))}
      cancelString={i18n.getString(_TL_('Cancel'))}
      onClickConfirm={onDelete}
      onClickCancel={onCancel}
      isUiBlocked={showUIBlocker}
      customStyle={{ zIndex: 1100000, background: 'rgba(255, 255, 255, 0.4)', overflowWrap: 'break-word' }}
    />
  ) : null;
};

DeleteConfirmDialog.propTypes = {
  showConfirmDialog: PropTypes.bool.isRequired,
  setShowConfirmDialog: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  deleteAssetApi: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export const useImageDeleteConfirmDialog = ({ dataService, i18n, onDeleted }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const itemToDeleteRef = useRef(null);

  const open = (item) => {
    itemToDeleteRef.current = item;
    setShowConfirmDialog(true);
  };

  const render = useCallback(() => {
    const deleteImageAsync = () => {
      if (itemToDeleteRef.current) {
        const { id: assetId, folderAssetType } = itemToDeleteRef.current || {};
        return dataService.deleteAsset({ assetId, folderAssetType });
      }
      return Promise.resolve();
    };

    const onSuccessCallback = () => {
      if (onDeleted) {
        onDeleted(itemToDeleteRef.current);
      }
    };

    return (<DeleteConfirmDialog
      showConfirmDialog={showConfirmDialog}
      setShowConfirmDialog={setShowConfirmDialog}
      deleteAssetApi={deleteImageAsync}
      onSuccess={onSuccessCallback}
      i18n={i18n}
    />);
  }, [dataService, i18n, onDeleted, showConfirmDialog]);

  return {
    open,
    render,
  };
};
